import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { DepositLimitCalculatorEvent } from "@obg/mat.ui.common";
import { DialogConfig } from "@obg/mat.ui.core";
import {
	CommonState,
	DEFAULT_MAX_DEPOSIT_LIMIT,
	DEPOSIT_LIMIT_CALCULATOR_MESSAGE_CONTEXT,
	DepositLimitCalculation,
	DepositLimitCalculationError,
	DepositLimitCalculatorAction,
	DepositLimitCalculatorMethods,
	DepositLimitCalculatorPredictionsQuery,
	DepositLimitCalculatorSelector,
	DepositLimitPeriods,
	DepositLimitPrediction,
	ResponsibleGamingUiConfigService,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	CurrencyConfigService,
	CurrencySelector,
	DepositLimit,
	DepositLimitAction,
	DepositLimitPeriodType,
	DepositLimitSelector,
	Jurisdiction,
	JurisdictionSelector,
	MarketSelector,
	MessageService,
	MessageType,
	MultiDepositLimitSelector,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content/public-api";
import { DeviceConfigService } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, merge, Observable, Subscription } from "rxjs";
import { filter, map, pairwise, startWith, switchMap, tap, debounceTime } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../containers/responsible-gaming-form.service";

import { DepositLimitCalculatorDateService } from "./deposit-limit-calculator-date.service";
import { DepositLimitCalculatorInfoModalComponent } from "./deposit-limit-calculator-info-modal/deposit-limit-calculator-info-modal.component";
import { DEPOSIT_SOURCE_TYPE_TRACKING_PAYLOAD, DEPOSIT_SOURCE_TYPE_VALUE } from "./deposit-limit-calculator.const";
import { DepositLimitCalculatorCalculateStatus, DepositLimitCalculatorTabName } from "./deposit-limit-calculator.model";

@Component({
	selector: "obg-m-deposit-limit-calculator",
	templateUrl: "./deposit-limit-calculator.container.html",
	styleUrls: ["./deposit-limit-calculator.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class DepositLimitCalculatorContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-deposit-limit-calculator-container") hostClass = true;

	readonly stretchedTabs: boolean;
	selectedTabIndex = 0;

	formGroupAmount: UntypedFormGroup;
	formGroupDate: UntypedFormGroup;
	currentFormType: DepositLimitCalculatorMethods | undefined;
	timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	query: DepositLimitCalculatorPredictionsQuery | undefined;
	predictions: DepositLimitPrediction[] | undefined;
	calculation: DepositLimitCalculation | undefined;
	remainingHoursSelectable: string[] | undefined;
	currencyCode: string | undefined;
	depositLimits: DepositLimit[] | undefined;
	depositLimitAmount = DEFAULT_MAX_DEPOSIT_LIMIT;
	startDate: Date | undefined;
	calculationError: DepositLimitCalculationError | undefined;
	isBusy$: Observable<boolean> | undefined;
	depositLimitPeriod: DepositLimitPeriods | DepositLimitPeriodType | undefined;

	private depositLimitType = this.responsibleGamingUiConfigService.getDepositLimitType();
	private data$$: Subscription | undefined;

	private readonly noLimitSetAmount = DEFAULT_MAX_DEPOSIT_LIMIT;
	private readonly formKeyDate = "date";
	private readonly formKeyTime = "time";

	constructor(
		private currencySelector: CurrencySelector,
		private store: Store<CommonState>,
		private deviceConfigService: DeviceConfigService,
		private depositLimitAction: DepositLimitAction,
		private formBuilder: UntypedFormBuilder,
		private changeDetectorRef: ChangeDetectorRef,
		private responsibleGamingUiConfigService: ResponsibleGamingUiConfigService,
		private depositLimitCalculatorDateService: DepositLimitCalculatorDateService,
		private translatableAlertService: AbstractTranslatableAlertService,
		private translateService: TranslateService,
		private depositLimitSelector: DepositLimitSelector,
		private responsibleGamingFormService: ResponsibleGamingFormService,
		private telemetryService: TelemetryService,
		private multiDepositLimitSelector: MultiDepositLimitSelector,
		private marketSelector: MarketSelector,
		private currencyConfig: CurrencyConfigService,
		private messageService: MessageService,
		private route: ActivatedRoute
	) {
		this.stretchedTabs = !this.deviceConfigService.isDesktop();

		this.formGroupAmount = this.formBuilder.group({
			amount: this.formBuilder.control("", [
				Validators.required,
				OBG_VALIDATORS.numeric,
				() => (this.depositLimitAmount === this.noLimitSetAmount ? { "no-limit-set": false } : null),
				OBG_VALIDATORS.numberSmallerOrEqual(() => this.depositLimitAmount),
				OBG_VALIDATORS.numericNotZero,
			]),
		});
		this.formGroupDate = this.formBuilder.group({
			date: this.formBuilder.control("", Validators.required),
			time: this.formBuilder.control("", Validators.required),
		});
	}

	ngOnInit(): void {
		this.reset();

		this.store.dispatch(this.depositLimitAction.getAll());

		const predictionError$ = this.messageService
			.consume$({
				context: [DEPOSIT_LIMIT_CALCULATOR_MESSAGE_CONTEXT.getPredictions],
				messageType: MessageType.Error,
			})
			.pipe(tap(() => this.setCalculationError(true)));

		const depositLimit$ = this.store.select(JurisdictionSelector.get).pipe(
			ignoreNil(),
			switchMap((jurisdiction) => {
				switch (jurisdiction) {
					case Jurisdiction.Sga:
						return this.store.select(this.multiDepositLimitSelector.get()).pipe(
							map((multiDepositLimits) => {
								const daily = multiDepositLimits?.depositLimit?.dailyDepositLimit;
								const weekly = multiDepositLimits?.depositLimit?.weeklyDepositLimit;
								const monthly = multiDepositLimits?.depositLimit?.monthlyDepositLimit;
								this.depositLimitPeriod = daily
									? DepositLimitPeriods.Daily
									: weekly
										? DepositLimitPeriods.Weekly
										: DepositLimitPeriods.Monthly;
								const depositLimitAmounts = [daily, weekly, monthly].filter(Boolean) as number[];
								return depositLimitAmounts.length > 0 ? Math.min(...depositLimitAmounts) : 0;
							})
						);
					default:
						return this.store.select(this.depositLimitSelector.get(this.depositLimitType)).pipe(
							tap((depositLimit) => (this.depositLimitPeriod = depositLimit?.period)),
							map((depositLimit) => depositLimit?.amount)
						);
				}
			}),
			ignoreNil(),
			filter((depositLimitAmount) => depositLimitAmount > 0),
			tap((depositLimitAmount) => (this.depositLimitAmount = depositLimitAmount))
		);

		const remainingHoursSelectable$ = this.formGroupDate?.controls[
			DepositLimitCalculatorMethods.Date
		].valueChanges.pipe(
			map((date) => this.depositLimitCalculatorDateService.createHoursAvailable(date)),
			tap((hours) => (this.remainingHoursSelectable = hours))
		);

		const currencyCode$ = combineLatest([
			this.store.select(this.currencySelector.getByCurrentCustomer()).pipe(ignoreNil()),
			this.store.select(this.marketSelector.getCurrent()),
		]).pipe(
			map(([currency, market]) => {
				const maskableMarket = this.currencyConfig.getMaskableMarket();
				const currencyMask = this.currencyConfig.getCurrencyCodeMaskingMap()[currency.code];
				if (market.slug === maskableMarket && currencyMask) {
					this.currencyCode = currencyMask;
				} else {
					this.currencyCode = currency.code;
				}
			})
		);

		const predictions$ = this.store.select(DepositLimitCalculatorSelector.getPredictions).pipe(
			ignoreNil(),
			map((predictions) => (this.predictions = predictions))
		);

		const triggerCalculation$ = this.store.select(DepositLimitCalculatorSelector.getCalculateReady).pipe(
			filter((ready) => ready),
			tap(() => this.calculateWithPredictions())
		);

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => {
				this.formGroupAmount?.reset();
				this.formGroupDate?.reset();
			})
		);

		const formGroupsValueChange$ = merge(this.formGroupAmount.valueChanges, this.formGroupDate.valueChanges).pipe(
			ignoreNil(),
			startWith({}),
			pairwise(),
			map(([prev, curr]) => Object.keys(curr).find((key) => !!curr[key] && curr[key] !== prev[key])),
			filter((key) => !!key),
			tap((key: string) => {
				if (
					(key === this.formKeyDate || key === this.formKeyTime) &&
					this.depositLimitAmount === this.noLimitSetAmount
				) {
					this.calculationError = {
						key: "deposit-limit-calculator.amount.no-limit-set",
						data: { noLimitError: true },
					};
				}
			})
		);

		this.isBusy$ = this.store.select(DepositLimitCalculatorSelector.isBusy);

		const depositSourceTypeQueryParams$ = this.route.queryParams.pipe(
			filter((params) => params?.source === DEPOSIT_SOURCE_TYPE_VALUE),
			tap(() => this.telemetryService.track(DEPOSIT_SOURCE_TYPE_TRACKING_PAYLOAD))
		);

		this.data$$ = merge(
			remainingHoursSelectable$,
			depositLimit$,
			predictions$,
			currencyCode$,
			triggerCalculation$,
			resetForm$,
			formGroupsValueChange$,
			predictionError$,
			depositSourceTypeQueryParams$
		).subscribe(() => this.changeDetectorRef.markForCheck());

		this.updateStartDate();
		this.monitorAmountControl();
	}

	updateStartDate() {
		this.startDate = this.depositLimitCalculatorDateService.getMinimumStartDate(new Date());
	}

	onInputChange(controlName: string): void {
		// The reason for this that this gtm event is pushed here, and not on the formGroup valueChanges
		// is that if we do it there, the gtm event will be pushed multiple times, on every keystroke,
		// but we need to push it only when the input value changes and is focused out.
		const inputName = `${controlName.charAt(0).toUpperCase()}${controlName.slice(1)}`;
		this.gtmEventAction(DepositLimitCalculatorEvent.FieldChange, undefined, inputName);
	}

	onCalculate(query: DepositLimitCalculatorPredictionsQuery): void {
		this.calculationError = undefined;
		this.query = query;
		this.currentFormType = query.type;
		this.store.dispatch(DepositLimitCalculatorAction.getPredictions());
		this.gtmEventAction(DepositLimitCalculatorEvent.Calculate, DepositLimitCalculatorCalculateStatus.Submitted);
	}

	calculateWithPredictions(): void {
		let match: DepositLimitPrediction | undefined;
		const calculateByDate = this.currentFormType === DepositLimitCalculatorMethods.Date;
		if (calculateByDate) {
			const date = this.query?.value.date;
			const time = this.query?.value.time;
			if (date && time) {
				const utcDate = this.depositLimitCalculatorDateService.convertDateToUtc(date, time, this.timeZone);
				const { error, convertedDate } = utcDate;
				if (!error && convertedDate) {
					match = this.getMatchingPredictionByDate(convertedDate);
				}
			}
		} else {
			const amount = this.query!.value.amount!;
			match = this.getMatchingPredictionByAmount(amount);
		}

		if (!match) {
			this.setCalculationError();
			return;
		}

		this.setCalculationResult(match, calculateByDate);
		this.gtmEventAction(DepositLimitCalculatorEvent.Calculate, DepositLimitCalculatorCalculateStatus.Approved);
	}

	getMatchingPredictionByAmount(amount: number): DepositLimitPrediction | undefined {
		return this.predictions?.find((prediction) => prediction.amount >= amount);
	}

	getMatchingPredictionByDate(date: Date): DepositLimitPrediction | undefined {
		return this.predictions?.reverse().find((prediction) => {
			const from = new Date(prediction.from);
			return from <= date;
		});
	}

	setCalculationResult(match: DepositLimitPrediction, isDate: boolean): void {
		this.calculation = isDate ? this.calculateByDate(match) : this.calculateByAmount(match);
		this.query = undefined;
	}

	selectedTabClick(index: number): void {
		this.selectedTabChanged(index);
		this.reset();
	}

	openInfoModal(): void {
		const dialogConfig: Partial<DialogConfig> = {
			panelClass: "obg-m-deposit-limit-calculator-info-modal",
			autoFocus: false,
		};
		this.translatableAlertService.open(
			{
				id: "deposit-limit-calculator-info-modal",
				messageComponent: DepositLimitCalculatorInfoModalComponent,
				dialogConfig,
				hideClose: true,
			},
			[TranslationGroup.General]
		);
	}

	ngOnDestroy(): void {
		this.data$$?.unsubscribe();
	}

	reset(formGroupDirective?: FormGroupDirective) {
		this.clearForms();
		formGroupDirective?.resetForm();
		this.clearCalculationVariables();
		this.clearDepositLimitCalculatorState();
	}

	private setCalculationError(isPredictionError = false) {
		const isCalculateByAmount = this.currentFormType === DepositLimitCalculatorMethods.Amount;
		const calculationError: DepositLimitCalculationError = { key: "deposit-limit-calculator.calculation.error" };
		if (isCalculateByAmount && !isPredictionError) {
			calculationError.key = "deposit-limit-calculator.deposit.max";
			calculationError.data = { period: this.depositLimitPeriod, amount: this.query?.value.amount };
		}
		if (this.depositLimitAmount === 0) {
			calculationError.key = "deposit-limit-calculator.amount.numberSmallerOrEqual";
			calculationError.data = { max: 0 };
		}
		this.calculationError = calculationError;
		this.gtmEventAction(DepositLimitCalculatorEvent.Calculate, DepositLimitCalculatorCalculateStatus.Disapproved);
		this.changeDetectorRef.markForCheck();
	}

	/**
	 * Create stringified formatted date and time as an array with this order. Otherwise returns an empty array.
	 */
	private createFormattedDateAndTime(date: Date | undefined, time: string | undefined) {
		if (!date || !time) {
			return [];
		}

		const formattedFullDateString = this.depositLimitCalculatorDateService.formatDate(
			date,
			"fullDate",
			this.timeZone,
			time
		);
		const dateAndTimeString = this.depositLimitCalculatorDateService.createDateAndTimeString(date, time);
		const formattedTimeString = this.depositLimitCalculatorDateService.formatTime(new Date(dateAndTimeString));

		return [formattedFullDateString, formattedTimeString];
	}

	private calculateByDate(match: DepositLimitPrediction): DepositLimitCalculation {
		let header: DepositLimitCalculation["header"] = "";
		let data: DepositLimitCalculation["data"] = { result: "" };

		const date = this.query!.value.date!;
		const time = this.query!.value.time!;
		const [dateString, timeString] = this.createFormattedDateAndTime(date, time);

		if (match.amount === 0) {
			header = this.translateService.instant("deposit-limit-calculator.result.limit-reached", {
				date: `${dateString}, ${timeString}`,
			});
		} else {
			const textValue = `${dateString} @ ${timeString}`;
			const amount = `${this.currencyCode} ${match.amount}`;
			header = this.constructResultText(textValue);
			data = { result: amount };
		}

		return { header, data };
	}

	private calculateByAmount(match: DepositLimitPrediction) {
		const amount = this.query!.value.amount!;
		const header = this.constructResultText(amount);
		const matchingDate = new Date(match.from);
		const formattedDate = this.depositLimitCalculatorDateService.formatDate(
			matchingDate,
			"fullDate",
			this.timeZone
		);
		const time = this.depositLimitCalculatorDateService.formatTime(matchingDate);
		const data = { recap: `${formattedDate}`, result: `@ ${time}` };
		return { header, data };
	}

	private constructResultText(valueToInsert: string | number | null): string {
		const valueByType =
			this.currentFormType === DepositLimitCalculatorMethods.Amount
				? `${this.currencyCode} ${valueToInsert}`
				: `${valueToInsert}`;
		return this.translateService.instant(`deposit-limit-calculator.result.${this.currentFormType}`, {
			value: valueByType,
		});
	}

	private clearDepositLimitCalculatorState() {
		this.store.dispatch(DepositLimitCalculatorAction.clear());
	}

	private clearCalculationVariables(): void {
		this.calculation = undefined;
		this.query = undefined;
	}

	private clearForms(): void {
		this.formGroupAmount?.reset();
		this.formGroupDate?.reset();
	}

	private gtmEventAction(
		event: DepositLimitCalculatorEvent,
		status?: DepositLimitCalculatorCalculateStatus,
		fieldName?: string
	) {
		const optionalParameters = {
			...(status && {
				Status: status,
				Currency: this.currencyCode ?? "",
				DepositLimitAmount: this.depositLimitAmount,
				TabName: this.selectedTabIndex
					? DepositLimitCalculatorTabName.ByDate
					: DepositLimitCalculatorTabName.ByAmount,
			}),
			...(fieldName && { FieldName: fieldName }),
		};

		this.telemetryService.track({
			event,
			...optionalParameters,
		});
	}

	private selectedTabChanged(index: number): void {
		this.calculationError = undefined;
		this.selectedTabIndex = index;
		this.changeDetectorRef.markForCheck();
	}

	private monitorAmountControl(): void {
		const amountControl = this.formGroupAmount.get("amount");

		const markTouchedOnFirstInput$$ = amountControl?.valueChanges
			.pipe(
				debounceTime(300),
				tap(() => {
					if (amountControl?.value && !amountControl.touched) {
						amountControl.markAsTouched();
						amountControl.updateValueAndValidity();
					}
				})
			)
			.subscribe();

		this.data$$?.add(markTouchedOnFirstInput$$);
	}
}
